import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, Star, CheckCircle2, Smile } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

function Stars({ value, onChange, max = 5, testid }) {
  return (
    <div className="flex gap-1" data-testid={testid}>
      {Array.from({ length: max }).map((_, i) => (
        <button key={i} type="button" onClick={() => onChange(i + 1)} className="transition-transform hover:scale-110">
          <Star size={26} className={i < value ? "fill-amber-400 text-amber-400" : "text-slate-300"} />
        </button>
      ))}
    </div>
  );
}

export default function SatisfactionSurvey() {
  const [sp] = useSearchParams();
  const token = sp.get("t") || "";
  const [form, setForm] = useState(null);
  const [service, setService] = useState("");
  const [scores, setScores] = useState({});
  const [glob, setGlob] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { api.get(`/satisfaction/form?token=${token}`).then(({ data }) => setForm(data)).catch(() => setForm(false)); }, [token]);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/satisfaction/responses", { service, scores, note_globale: glob, commentaire: comment, token });
      setDone(true);
    } catch (e) { toast.error(apiError(e, "Envoi impossible")); } finally { setBusy(false); }
  };

  if (form === null) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="animate-spin text-ciel" /></div>;
  if (done) return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
      <div className="max-w-md rounded-2xl border border-slate-200 bg-white p-10 text-center" data-testid="survey-thanks">
        <CheckCircle2 size={48} className="mx-auto text-emerald-500" />
        <h1 className="mt-4 font-display text-xl font-bold text-navy">Merci pour votre retour !</h1>
        <p className="mt-2 text-sm text-slate-500">Votre avis nous aide à améliorer la qualité de nos soins.</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-10">
      <div className="mx-auto max-w-lg rounded-2xl border border-slate-200 bg-white p-6 sm:p-8" data-testid="survey-form">
        <div className="mb-5 flex items-center gap-2"><Smile className="text-ciel" size={24} /><div>
          <h1 className="font-display text-lg font-bold text-navy">Enquête de satisfaction</h1>
          <p className="text-xs text-slate-500">Cabinet CARIS Santé · 1 minute</p></div></div>

        <label className="text-sm font-medium text-slate-700">Service concerné</label>
        <select value={service} onChange={(e) => setService(e.target.value)} data-testid="survey-service" className="mt-1 mb-4 h-10 w-full rounded-lg border border-slate-200 px-3 text-sm">
          <option value="">— Sélectionner —</option>{(form.services || []).map((s) => <option key={s}>{s}</option>)}
        </select>

        <div className="space-y-4">
          {(form.sections || []).map((s) => (
            <div key={s.key} className="flex items-center justify-between">
              <span className="text-sm text-slate-700">{s.label}</span>
              <Stars value={scores[s.key] || 0} onChange={(v) => setScores((c) => ({ ...c, [s.key]: v }))} testid={`survey-score-${s.key}`} />
            </div>
          ))}
        </div>

        <div className="mt-5">
          <label className="text-sm font-medium text-slate-700">Note globale (sur 10)</label>
          <div className="mt-1 flex flex-wrap gap-1" data-testid="survey-global">
            {Array.from({ length: 10 }).map((_, i) => (
              <button key={i} type="button" onClick={() => setGlob(i + 1)} className={`h-9 w-9 rounded-lg text-sm font-semibold ${glob >= i + 1 ? "bg-ciel text-white" : "bg-slate-100 text-slate-500"}`}>{i + 1}</button>
            ))}
          </div>
        </div>

        <div className="mt-5">
          <label className="text-sm font-medium text-slate-700">Commentaires libres</label>
          <Textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3} data-testid="survey-comment" className="mt-1" placeholder="Votre expérience, suggestions…" />
        </div>

        <Button onClick={submit} disabled={busy || !glob} data-testid="survey-submit" className="mt-6 w-full rounded-full bg-ciel text-white hover:bg-sky-700">
          {busy ? <Loader2 size={16} className="animate-spin" /> : "Envoyer mon avis"}
        </Button>
      </div>
    </div>
  );
}
